import {
	type ProjectDTO,
	TICKET_LIMITS,
	type TicketDTO,
	type TicketStatus,
	type TopicDTO,
} from "@zplanem/responses"
import { webPaths } from "@zplanem/web-paths"
import { type DragEvent, type FormEvent, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ApiError } from "../../../lib/api-client"
import { TICKET_BOARD_STATUSES } from "../../hooks/ticket-api-paths"
import { usePropertyMemberships } from "../../hooks/use-property-memberships"
import {
	useCreateTicket,
	usePropertyTickets,
	useSetAnyTicketStatus,
	useSetTicketStatus,
} from "../../hooks/use-tickets"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useUser } from "../../user-context"
import {
	TicketCard,
	type TicketReturnTo,
	TicketSkeletonCards,
} from "./ticket-card"
import {
	hasTicketBoardFilters,
	quickCreateTicketBody,
	type TicketBoardFilters,
	topicLabelForTicket,
} from "./ticket-filter-model"
import {
	canCreateTicketInBoardStatus,
	canMoveTicketToStatus,
	getAllowedTicketStatusTargets,
	inlineCreateTicketButtonLabel,
	isTicketManager,
} from "./ticket-form-model"
import { TicketStatusMenu } from "./ticket-status-menu"
import { flattenTicketPages } from "./ticket-view-model"

export function TicketBoard({
	propertyId,
	filters,
	topics,
	projects = [],
	showProjectLabels = true,
	returnTo,
	lockedProjectId,
}: {
	propertyId: string
	filters: TicketBoardFilters
	topics: TopicDTO[]
	projects?: ProjectDTO[]
	showProjectLabels?: boolean
	returnTo?: TicketReturnTo
	lockedProjectId?: string
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const { profile } = useUser()
	const memberships = usePropertyMemberships(propertyId)
	const canManageTickets = isTicketManager(memberships.data, profile?.id)
	const moveStatus = useSetAnyTicketStatus(propertyId, filters)
	const [draggedTicket, setDraggedTicket] = useState<TicketDTO | null>(null)
	const [boardError, setBoardError] = useState<string | null>(null)
	const hasFilters = hasTicketBoardFilters(filters, lockedProjectId)

	async function moveTicketToStatus(ticket: TicketDTO, status: TicketStatus) {
		if (!canMoveTicketToStatus(ticket, status, canManageTickets, profile?.id))
			return
		setBoardError(null)
		try {
			await moveStatus.mutateAsync({ ticketId: ticket.id, status })
		} catch (err) {
			setBoardError(
				err instanceof ApiError ? err.message : t.tickets.statusChangeError,
			)
		} finally {
			setDraggedTicket(null)
		}
	}

	return (
		<div>
			{boardError ? (
				<div
					role="alert"
					className="mb-4 rounded-lg border border-destructive bg-destructive px-3 py-2 text-sm text-destructive-foreground"
				>
					{boardError}
				</div>
			) : null}
			<section
				aria-label={t.tickets.boardAriaLabel}
				className="grid gap-4 xl:grid-cols-4 md:grid-cols-2"
			>
				{TICKET_BOARD_STATUSES.map((status) => (
					<TicketStatusColumn
						key={status}
						propertyId={propertyId}
						status={status}
						filters={filters}
						topics={topics}
						projects={projects}
						showProjectLabels={showProjectLabels}
						returnTo={returnTo}
						hasFilters={hasFilters}
						viewerId={profile?.id}
						canManageTickets={canManageTickets}
						draggedTicket={draggedTicket}
						dragPending={moveStatus.isPending}
						onDragStart={setDraggedTicket}
						onDragEnd={() => setDraggedTicket(null)}
						onDropTicket={(ticket, nextStatus) => {
							void moveTicketToStatus(ticket, nextStatus)
						}}
					/>
				))}
			</section>
		</div>
	)
}

function TicketStatusColumn({
	propertyId,
	status,
	filters,
	topics,
	projects,
	showProjectLabels,
	returnTo,
	hasFilters,
	viewerId,
	canManageTickets,
	draggedTicket,
	dragPending,
	onDragStart,
	onDragEnd,
	onDropTicket,
}: {
	propertyId: string
	status: TicketStatus
	filters: TicketBoardFilters
	topics: TopicDTO[]
	projects: ProjectDTO[]
	showProjectLabels: boolean
	returnTo?: TicketReturnTo
	hasFilters: boolean
	viewerId: string | undefined
	canManageTickets: boolean
	draggedTicket: TicketDTO | null
	dragPending: boolean
	onDragStart: (ticket: TicketDTO) => void
	onDragEnd: () => void
	onDropTicket: (ticket: TicketDTO, status: TicketStatus) => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const query = usePropertyTickets(propertyId, { ...filters, status })
	const tickets = flattenTicketPages(query.data?.pages ?? [])
	const acceptsDraggedTicket = draggedTicket
		? canMoveTicketToStatus(draggedTicket, status, canManageTickets, viewerId)
		: false

	return (
		<section
			aria-labelledby={`ticket-column-${status}`}
			onDragOver={(event) => {
				if (!acceptsDraggedTicket) return
				event.preventDefault()
				event.dataTransfer.dropEffect = "move"
			}}
			onDrop={(event) => {
				if (!draggedTicket || !acceptsDraggedTicket) return
				event.preventDefault()
				onDropTicket(draggedTicket, status)
			}}
			className={`min-h-64 rounded-lg border bg-muted/30 p-3 transition-colors ${
				acceptsDraggedTicket ? "border-ring bg-accent/20" : "border-border"
			}`}
		>
			<div className="flex items-center justify-between gap-2">
				<h2
					id={`ticket-column-${status}`}
					className="text-sm font-semibold text-foreground"
				>
					{t.tickets.status[status]}
				</h2>
				<span className="rounded-sm bg-background px-2 py-0.5 text-xs font-medium text-muted-foreground">
					<span className="sr-only">
						{t.tickets.statusCountLabel
							.replace("{count}", String(tickets.length))
							.replace("{status}", t.tickets.status[status])}
					</span>
					<span aria-hidden="true">{tickets.length}</span>
				</span>
			</div>

			<div className="mt-3 space-y-3">
				{query.isLoading && tickets.length === 0 ? (
					<TicketSkeletonCards />
				) : query.error && tickets.length === 0 ? (
					<div
						role="alert"
						className="rounded-lg border border-destructive bg-destructive px-3 py-3 text-sm text-destructive-foreground"
					>
						<div className="font-medium">{t.tickets.errorTitle}</div>
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => query.refetch()}
							className="mt-2"
						>
							{t.tickets.errorRetry}
						</Button>
					</div>
				) : tickets.length === 0 ? (
					<div className="rounded-lg border border-dashed border-border bg-background/60 px-3 py-6 text-center text-sm text-muted-foreground">
						<div className="font-medium text-foreground">
							{hasFilters ? t.tickets.emptyFilteredTitle : t.tickets.emptyTitle}
						</div>
						<div className="mt-1 text-xs">
							{hasFilters
								? t.tickets.emptyFilteredDescription
								: t.tickets.emptyDescription}
						</div>
					</div>
				) : (
					tickets.map((ticket) => (
						<TicketBoardCard
							key={ticket.id}
							ticket={ticket}
							topicLabel={topicLabelForTicket(ticket, topics)}
							projectLabel={
								showProjectLabels
									? projectLabelForTicket(ticket, projects)
									: null
							}
							projectHref={
								showProjectLabels && ticket.projectId
									? webPaths.project(ticket.propertyId, ticket.projectId)
									: null
							}
							returnTo={returnTo}
							viewerId={viewerId}
							canManageTickets={canManageTickets}
							dragged={draggedTicket?.id === ticket.id}
							dragPending={dragPending}
							onDragStart={onDragStart}
							onDragEnd={onDragEnd}
						/>
					))
				)}
			</div>

			{canCreateTicketInBoardStatus(status, canManageTickets) ? (
				<TicketInlineCreate
					propertyId={propertyId}
					status={status}
					filters={filters}
					returnTo={returnTo}
				/>
			) : null}

			{query.error && tickets.length > 0 ? (
				<div
					role="alert"
					className="mt-3 rounded-lg border border-destructive bg-destructive px-3 py-3 text-sm text-destructive-foreground"
				>
					<div className="font-medium">{t.tickets.errorTitle}</div>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => query.refetch()}
						className="mt-2"
					>
						{t.tickets.errorRetry}
					</Button>
				</div>
			) : null}

			{query.hasNextPage ? (
				<Button
					type="button"
					variant="outline"
					size="sm"
					className="mt-3 w-full"
					disabled={query.isFetchingNextPage}
					onClick={() => {
						void query.fetchNextPage()
					}}
				>
					{query.isFetchingNextPage
						? t.tickets.loadingMore
						: t.tickets.loadMore}
				</Button>
			) : null}
		</section>
	)
}

function TicketBoardCard({
	ticket,
	topicLabel,
	projectLabel,
	projectHref,
	returnTo,
	viewerId,
	canManageTickets,
	dragged,
	dragPending,
	onDragStart,
	onDragEnd,
}: {
	ticket: TicketDTO
	topicLabel?: string | null
	projectLabel?: string | null
	projectHref?: string | null
	returnTo?: TicketReturnTo
	viewerId: string | undefined
	canManageTickets: boolean
	dragged: boolean
	dragPending: boolean
	onDragStart: (ticket: TicketDTO) => void
	onDragEnd: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const setStatus = useSetTicketStatus(ticket.propertyId, ticket.id)
	const [error, setError] = useState<string | null>(null)
	const canSetStatus =
		canManageTickets || (viewerId != null && ticket.assignee?.id === viewerId)
	const allowedStatuses = getAllowedTicketStatusTargets(
		ticket.status,
		canManageTickets,
	)
	const canDrag = canSetStatus && allowedStatuses.length > 1 && !dragPending

	return (
		<div>
			<TicketCard
				ticket={ticket}
				t={t.tickets}
				locale={locale}
				topicLabel={topicLabel}
				projectLabel={projectLabel}
				projectHref={projectHref}
				returnTo={returnTo}
				draggable={canDrag}
				dragging={dragged}
				onDragStart={(event: DragEvent<HTMLElement>) => {
					if (!canDrag) return
					event.dataTransfer.effectAllowed = "move"
					event.dataTransfer.setData("text/plain", ticket.id)
					onDragStart(ticket)
				}}
				onDragEnd={onDragEnd}
				statusControl={
					canSetStatus && allowedStatuses.length > 1 ? (
						<TicketStatusMenu
							status={ticket.status}
							statuses={allowedStatuses}
							pending={setStatus.isPending}
							onSelect={async (status) => {
								setError(null)
								try {
									await setStatus.mutateAsync({ status })
								} catch (err) {
									setError(
										err instanceof ApiError
											? err.message
											: t.tickets.statusChangeError,
									)
								}
							}}
						/>
					) : undefined
				}
			/>
			{error ? (
				<div
					role="alert"
					className="mt-2 rounded-lg border border-destructive bg-destructive px-3 py-2 text-xs text-destructive-foreground"
				>
					{error}
				</div>
			) : null}
		</div>
	)
}

function projectLabelForTicket(
	ticket: Pick<TicketDTO, "projectId">,
	projects: readonly ProjectDTO[],
): string | null {
	if (!ticket.projectId) return null
	return (
		projects.find((project) => project.id === ticket.projectId)?.name ?? null
	)
}

function TicketInlineCreate({
	propertyId,
	status,
	filters,
	returnTo,
}: {
	propertyId: string
	status: TicketStatus
	filters: TicketBoardFilters
	returnTo?: TicketReturnTo
}) {
	const navigate = useNavigate()
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const createTicket = useCreateTicket(propertyId)
	const moveStatus = useSetAnyTicketStatus(propertyId, filters)
	const [expanded, setExpanded] = useState(false)
	const [title, setTitle] = useState("")
	const [error, setError] = useState<string | null>(null)
	const pending = createTicket.isPending || moveStatus.isPending

	async function onSubmit(event: FormEvent) {
		event.preventDefault()
		const trimmedTitle = title.trim()
		if (!trimmedTitle) {
			setError(t.tickets.formErrors.titleRequired)
			return
		}
		if (trimmedTitle.length > TICKET_LIMITS.titleMax) {
			setError(
				t.tickets.formErrors.titleTooLong.replace(
					"{max}",
					String(TICKET_LIMITS.titleMax),
				),
			)
			return
		}

		setError(null)
		try {
			const created = await createTicket.mutateAsync(
				quickCreateTicketBody(trimmedTitle, filters),
			)
			if (status !== "open") {
				await moveStatus.mutateAsync({
					ticketId: created.ticket.id,
					status,
				})
			}
			setTitle("")
			setExpanded(false)
			navigate(webPaths.ticket(propertyId, created.ticket.id), {
				state: returnTo ? { returnTo } : undefined,
			})
		} catch (err) {
			setError(
				err instanceof ApiError ? err.message : t.tickets.formErrors.unknown,
			)
		}
	}

	if (!expanded) {
		return (
			<Button
				type="button"
				variant="ghost"
				size="sm"
				className="mt-3 w-full justify-start border border-dashed border-border text-muted-foreground hover:text-foreground"
				aria-label={inlineCreateTicketButtonLabel(
					t.tickets.createCta,
					t.tickets.status[status],
				)}
				onClick={() => setExpanded(true)}
			>
				+ {t.tickets.createCta}
			</Button>
		)
	}

	return (
		<form onSubmit={onSubmit} className="mt-3 space-y-2" noValidate>
			<input
				type="text"
				value={title}
				onChange={(event) => setTitle(event.target.value)}
				placeholder={t.tickets.titlePlaceholder}
				maxLength={TICKET_LIMITS.titleMax + 10}
				aria-label={t.tickets.titleLabel}
				aria-invalid={Boolean(error)}
				className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-ring focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
			/>
			{error ? (
				<div role="alert" className="text-xs text-destructive">
					{error}
				</div>
			) : null}
			<div className="flex justify-end gap-2">
				<Button
					type="button"
					variant="ghost"
					size="sm"
					disabled={pending}
					onClick={() => {
						setExpanded(false)
						setTitle("")
						setError(null)
					}}
				>
					{t.projects.form.cancel}
				</Button>
				<Button type="submit" variant="brand" size="sm" disabled={pending}>
					{pending ? t.tickets.creating : t.tickets.submitCreate}
				</Button>
			</div>
		</form>
	)
}
